import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layouts from "../layouts/Layouts";
import { fetchServices } from "../locales/services";
import { Typography, CircularProgress, useMediaQuery } from "@mui/material";
import AppointmentForm from "./Appointment";
import { Button } from "react-bootstrap";
import { TbTopologyStarRing3 } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import ServiceTypes from "./ServiceTypes";
import { Carousel } from "react-bootstrap";

const ServiceDetails = () => {
  const location = useLocation();
  const [updatedServices, setUpdatedServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const appointmentRef = useRef(null);
  const serviceSectionRef = useRef(null);
  const isFirstRender = useRef(true);
  const { t } = useTranslation();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const serviceId = query.get("serviceId");

  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:868px)");

  useEffect(() => {
    const loadServices = async (id) => {
      setIsLoading(true);
      const data = await fetchServices();
      setUpdatedServices(data);
      if (location.state?.service) {
        setSelectedService(location.state?.service);
      }
      setIsLoading(false);
    };

    loadServices(serviceId);
  }, [location.state?.service, serviceId]);

  useEffect(() => {
    if (selectedService && serviceSectionRef.current && isFirstRender.current) {
      const scrollDelay = isFirstRender.current ? 1000 : 500;
      const timeoutId = setTimeout(() => {
        serviceSectionRef?.current?.scrollIntoView({
          behavior: "smooth",
        });
      }, scrollDelay);

      // if (isFirstRender.current) {

      //   isFirstRender.current = false;
      // }

      return () => clearTimeout(timeoutId);
    }
  }, [selectedService]);

  const handleGetAppointmentClick = (e) => {
    e.preventDefault();
    if (appointmentRef.current) {
      appointmentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const hasNumberedBulletPoints = (service) => {
    return Object.keys(service).some(
      (key) => key.startsWith("bulletPoints") && key !== "bulletPoints"
    );
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);

    if (isMobile && serviceSectionRef.current) {
      serviceSectionRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const renderParagraphsAndBullets = () => {
    if (!selectedService) return null;

    const isNumbered = hasNumberedBulletPoints(selectedService);
    const content = [];

    if (isNumbered) {
      let i = 1;
      while (
        selectedService[`paragraph${i}`] ||
        selectedService[`bulletPoints${i}`]
      ) {
        if (selectedService[`paragraph${i}`]) {
          content.push(
            <p
              key={`paragraph-${i}`}
              style={{ fontSize: "18px", marginTop: "15px" }}
            >
              {t(
                `servicesPage.service${selectedService.sortOrder}.paragraph${i}`
              )}
            </p>
          );
        }

        if (selectedService[`bulletPoints${i}`]) {
          const translatedBulletPoints = t(
            `servicesPage.service${selectedService.sortOrder}.bulletPoints${i}`,
            {
              returnObjects: true,
            }
          );

          if (
            Array.isArray(translatedBulletPoints) &&
            translatedBulletPoints.length > 0
          ) {
            content.push(
              <ul key={`bullets-${i}`} style={{ marginTop: "15px" }}>
                {translatedBulletPoints.map((point, index) => (
                  <li key={`${i}-${index}`}>
                    <TbTopologyStarRing3
                      style={{
                        marginRight: "8px",
                        fontSize: "23px",
                        color: "var(--color-doctor)",
                      }}
                    />
                    {point}
                  </li>
                ))}
              </ul>
            );
          }
        }
        i++;
      }
    }
     else {
      let i = 1;
      while (selectedService[`paragraph${i}`]) {
        content.push(
          <p key={`paragraph-${i}`} style={{ fontSize: "18px" }}>
            {t(
              `servicesPage.service${selectedService.sortOrder}.paragraph${i}`
            )}
          </p>
        );
        i++;
      }

      if (selectedService.bulletPoints) {
        const translatedBulletPoints = t(
          `servicesPage.service${selectedService.sortOrder}.bulletPoints`,
          {
            returnObjects: true,
          }
        );

        if (
          Array.isArray(translatedBulletPoints) &&
          translatedBulletPoints.length > 0
        ) {
          content.push(
            <ul key="bullets" style={{ marginTop: "35px" }}>
              {translatedBulletPoints.map((point, index) => (
                <li key={index}>
                  <TbTopologyStarRing3
                    style={{
                      marginRight: "8px",
                      fontSize: "23px",
                      color: "var(--color-doctor)",
                    }}
                  />
                  {point}
                </li>
              ))}
            </ul>
          );
        }
      }
    }

    return content;
  };

  if (isLoading) {
    return (
      <Layouts noheader footer={2}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </div>
      </Layouts>
    );
  }

  if (!selectedService) {
    navigate("/services");
    return (
      <Layouts noheader footer={2}>
        <Typography variant="h6" color="textSecondary">
          No service selected. Please choose a service.
        </Typography>
      </Layouts>
    );
  }

  return (
    <Layouts footer={2} noheader>
      <section
        className="services-area section-gap"
        style={{ paddingBottom: "0" }}
        ref={serviceSectionRef}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 order-lg-last">
              <div className="service-details-wrapper">
                <h2 className="service-title">
                  {t(
                    `servicesPage.service${selectedService.sortOrder}.${selectedService?.title}`
                  ) || <CircularProgress color="var(--color-fourth)" />}
                </h2>

                {/* Carousel Component */}
                <Carousel
                  fade
                  indicators={false}
                  controls={!!selectedService?.image2}
                  interval={5000}
                  style={{
                    width: "100%",
                    height: "auto",
                    marginBottom: "2rem",
                  }}
                >
                  {/* First Carousel Item - Main Image */}
                  <Carousel.Item>
                    {isLoading ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "500px",
                        }}
                      >
                        <CircularProgress color="var(--color-fourth)" />
                      </div>
                    ) : (
                      <img
                        src={selectedService?.image}
                        alt={`${selectedService?.title} - Main`}
                        style={{
                          width: "100%",
                          height: "auto",
                          maxHeight: "500px",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </Carousel.Item>

                  {/* Second Carousel Item - Only render if image2 exists */}
                  {selectedService?.image2 && (
                    <Carousel.Item>
                      {isLoading ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "500px",
                          }}
                        >
                          <CircularProgress color="var(--color-fourth)" />
                        </div>
                      ) : (
                        <img
                          src={selectedService?.image2}
                          alt={`${selectedService?.title} - Additional`}
                          style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: "500px",
                            objectFit: "cover",
                          }}
                        />
                      )}
                    </Carousel.Item>
                  )}
                </Carousel>
                {isLoading ? (
                  <CircularProgress size={24} color="var(--color-fourth)" />
                ) : (
                  <>
                    <p style={{ fontSize: "18px" }}>
                      {t(
                        `servicesPage.service${selectedService.sortOrder}.${selectedService?.description}`
                      )}
                    </p>
                    {renderParagraphsAndBullets()}
                  </>
                )}
              </div>
            </div>

            <div className="col-lg-4 col-md-10 order-lg-first">
              <div className="service-sidebar">
                <ServiceTypes
                  updatedServices={updatedServices}
                  selectedService={selectedService}
                  setSelectedService={setSelectedService}
                  navigate={navigate}
                  handleServiceClick={handleServiceClick}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <div style={{ display: "flex", width: "100%", justifyContent: "end" }}>
        <Button
          onClick={handleGetAppointmentClick}
          style={{
            backgroundColor: "var(--color-fourth)",
            color: "white",
            marginTop: "50px",
            width: "100%",
            borderRadius: 0,
          }}
        >
          {t("serviceDetails.anyConcerns")}
          <i className="far fa-plus" />
        </Button>
      </div>
      <AppointmentForm ref={appointmentRef} side="right" imageNumber={2} />
    </Layouts>
  );
};

export default ServiceDetails;
